import { createElement, useCallback, useState } from 'react';
import { Modal, Tooltip, notification } from 'antd';
import { useQueryClient } from '@tanstack/react-query';
import type { MenuProps } from 'antd';
import { ppaSimulationUrls } from 'config/urls';
import { useCommonMutationHandler } from 'hooks/general/useCommonMutationHandler';
import { PricingSimulatorNotificationMessages } from 'utils/Enums';
import { getAuthUserMail, sendNotification } from 'utils/helperMethods';
import { resolvePpaCreateProjectApiErrorMessage, toPpaPublicRowRecord } from 'utils/ppaHelpers';
import {
    normalizePpaTableRowAction,
    PpaScenarioActionType,
    PpaTableRowAction,
    PpaTableRowType,
    tableRowActionToScenarioAction,
} from 'types/ppa';
import { SCENARIO_MANAGER_PROJECT_LIST_QUERY_KEY } from 'contexts/PriceSimulatorV3Context';
import {
    getActionIcon,
    scenarioDeleteConfirmIcon,
    scenarioDeleteSuccessIcon,
} from '../config/scenarioActions';
import type {
    DeleteScenarioResponse,
    InternalScenarioRecord,
    InternalTableRecord,
    ProjectRow,
    ScenarioModalState,
    ScenarioRow,
} from '../types';

type ProjectActionRecord = {
    project_id: number;
    project_name?: string;
};

type UseScenarioActionsParams = {
    projects: ProjectActionRecord[];
    childLimit?: number;
    scenariosByProjectMerged: Record<number, ScenarioRow[]>;
    scenarioCountByProject: Record<number, number>;
    actionOptions: string[];
    removeProjectFromScenarioState: (projectId: number) => void;
    removeScenarioFromProjectState: (projectId: number, scenarioId: number) => void;
    onExpandedProjectRemoved?: (projectId: number) => void;
    canAccessAction: (record: InternalTableRecord, actionLabel: string) => boolean;
    onAction?: (action: PpaTableRowAction, record: ProjectRow | ScenarioRow) => void;
};

export const useScenarioActions = ({
    projects,
    childLimit,
    scenariosByProjectMerged,
    scenarioCountByProject,
    actionOptions,
    removeProjectFromScenarioState,
    removeScenarioFromProjectState,
    onExpandedProjectRemoved,
    canAccessAction,
    onAction,
}: UseScenarioActionsParams) => {
    const queryClient = useQueryClient();
    const [scenarioModal, setScenarioModal] = useState<ScenarioModalState | null>(null);

    const isScenarioLimitReachedForProject = useCallback(
        (projectId: number) => {
            if (typeof childLimit !== 'number') return false;
            const scenarioCount =
                scenarioCountByProject[projectId] ?? scenariosByProjectMerged[projectId]?.length ?? 0;
            return scenarioCount >= childLimit;
        },
        [childLimit, scenarioCountByProject, scenariosByProjectMerged],
    );

    const openScenarioModal = useCallback(
        (projectId: number, actionType: PpaScenarioActionType, scenario?: ScenarioRow) => {
            if (actionType !== PpaScenarioActionType.Create && !scenario) return;
            if (actionType === PpaScenarioActionType.Create && isScenarioLimitReachedForProject(projectId)) {
                return;
            }
            if (actionType === PpaScenarioActionType.Duplicate && isScenarioLimitReachedForProject(projectId)) {
                return;
            }

            if (actionType === PpaScenarioActionType.Create) {
                setScenarioModal({ projectId, actionType: PpaScenarioActionType.Create, editParams: {} });
                return;
            }

            if (actionType === PpaScenarioActionType.Duplicate) {
                setScenarioModal({
                    projectId,
                    actionType: PpaScenarioActionType.Duplicate,
                    editParams: {
                        scenario_name: `${scenario!.scenario_name}_copy`,
                    },
                });
                return;
            }

            setScenarioModal({
                projectId,
                actionType: PpaScenarioActionType.Rename,
                editParams: {
                    scenario_id: scenario!.scenario_id,
                    scenario_name: scenario!.scenario_name,
                },
            });
        },
        [isScenarioLimitReachedForProject],
    );

    const { mutate: deleteScenarioMutate } = useCommonMutationHandler({
        url: ppaSimulationUrls.deleteScenarioUrl,
        method: 'post',
    });

    const deleteScenario = useCallback(
        (projectId: number, scenario: InternalScenarioRecord): Promise<void> => {
            const createdBy = getAuthUserMail();
            const payload: Record<string, unknown> = {
                scenario_id: scenario.scenario_id,
            };
            if (createdBy) {
                payload.created_by = createdBy;
            }

            return new Promise<void>((resolve, reject) => {
                deleteScenarioMutate(payload, {
                    onSuccess: (response) => {
                        const responseData = (response?.data ?? {}) as DeleteScenarioResponse;
                        const responseProjectId =
                            typeof responseData.project_id === 'number' ? responseData.project_id : projectId;
                        const projectDeleted = Boolean(responseData.project_deleted);
                        const scenarioDisplayName =
                            String(scenario.scenario_name ?? '').trim() || 'Scenario';

                        const projectDisplayName = projectDeleted
                            ? (
                                  projects.find(
                                      (project) => project.project_id === responseProjectId,
                                  )?.project_name ?? ''
                              ).trim() || 'the project'
                            : '';

                        if (projectDeleted) {
                            removeProjectFromScenarioState(responseProjectId);
                            onExpandedProjectRemoved?.(responseProjectId);
                            void queryClient.invalidateQueries({
                                queryKey: SCENARIO_MANAGER_PROJECT_LIST_QUERY_KEY,
                            });
                        } else {
                            removeScenarioFromProjectState(responseProjectId, scenario.scenario_id);
                        }

                        const notificationConfig = projectDeleted
                            ? {
                                  message: 'Project Deleted',
                                  description: `‘${scenarioDisplayName}’ was the last scenario in ‘${projectDisplayName}’. The project has also been deleted.`,
                              }
                            : {
                                  message: 'Scenario Deleted',
                                  description: `${scenarioDisplayName} has been deleted successfully`,
                              };

                        notification.success({
                            duration: 5,
                            icon: scenarioDeleteSuccessIcon,
                            ...notificationConfig,
                            style: { width: 350, height: 'auto', borderRadius: 8 },
                        });
                        resolve();
                    },
                    onError: (error: unknown) => {
                        const errorMsg = resolvePpaCreateProjectApiErrorMessage(
                            error,
                            PricingSimulatorNotificationMessages.SCENARIO_ACTION_ERROR,
                        );
                        sendNotification('error', errorMsg);
                        reject(error);
                    },
                });
            });
        },
        [
            deleteScenarioMutate,
            projects,
            queryClient,
            removeProjectFromScenarioState,
            removeScenarioFromProjectState,
            onExpandedProjectRemoved,
        ],
    );

    const confirmDeleteScenario = useCallback(
        (projectId: number, scenario: InternalScenarioRecord) => {
            const currentScenarioCount =
                scenarioCountByProject[projectId] ?? scenariosByProjectMerged[projectId]?.length;
            const isDeletingLastScenario =
                typeof currentScenarioCount === 'number' && currentScenarioCount <= 1;
            const deleteConfirmationMessage = isDeletingLastScenario
                ? 'Are you sure you want to delete this scenario? This is the last scenario in the project, so deleting it will also delete the project.'
                : 'Are you sure you want to delete this scenario?';

            Modal.confirm({
                title: 'Delete Scenario',
                content: deleteConfirmationMessage,
                icon: scenarioDeleteConfirmIcon,
                className: 'price-simulator-v3-delete-scenario-modal',
                centered: true,
                okText: 'Delete',
                cancelText: 'Cancel',
                okButtonProps: { danger: true, style: { borderRadius: 8 } },
                cancelButtonProps: { style: { borderRadius: 8 } },
                onOk: () => deleteScenario(projectId, scenario),
            });
        },
        [deleteScenario, scenarioCountByProject, scenariosByProjectMerged],
    );

    const getActionMenuItems = useCallback(
        (record: InternalTableRecord): MenuProps['items'] =>
            actionOptions
                .map((item) => ({ item, action: normalizePpaTableRowAction(item) }))
                .filter(({ action }) => {
                    if (
                        record.row_type === PpaTableRowType.Scenario &&
                        action === PpaTableRowAction.Delete
                    ) {
                        const scenarioCount =
                            scenarioCountByProject[record.project_id] ??
                            scenariosByProjectMerged[record.project_id]?.length ??
                            0;
                        return scenarioCount > 1;
                    }
                    if (record.row_type !== PpaTableRowType.Project || !action) {
                        return true;
                    }
                    return action !== PpaTableRowAction.Duplicate && action !== PpaTableRowAction.Delete;
                })
                .filter(({ item }) => canAccessAction(record, item))
                .map(({ item, action }) => {
                    const isDuplicateDisabled =
                        record.row_type === PpaTableRowType.Scenario &&
                        action === PpaTableRowAction.Duplicate &&
                        isScenarioLimitReachedForProject(record.project_id);
                    const disabledTooltipTitle =
                        isDuplicateDisabled && typeof childLimit === 'number'
                            ? `You can only create ${childLimit} scenarios`
                            : '';

                    return {
                        key: item,
                        icon: getActionIcon(item),
                        label: disabledTooltipTitle
                            ? createElement(
                                  Tooltip,
                                  {
                                      title: disabledTooltipTitle,
                                      color: '#000000',
                                      placement: 'top',
                                      overlayInnerStyle: { borderRadius: 8, fontSize: 16, fontWeight: 500 },
                                  },
                                  createElement('span', null, item),
                              )
                            : item,
                        danger: action === PpaTableRowAction.Delete,
                        disabled: isDuplicateDisabled,
                    };
                }),
        [
            actionOptions,
            canAccessAction,
            childLimit,
            isScenarioLimitReachedForProject,
            scenarioCountByProject,
            scenariosByProjectMerged,
        ],
    );

    const handleRowAction = useCallback(
        (action: string, record: InternalTableRecord) => {
            if (!canAccessAction(record, action)) {
                return;
            }
            const normalizedAction = normalizePpaTableRowAction(action);
            if (!normalizedAction) {
                return;
            }

            if (record.row_type === PpaTableRowType.Scenario) {
                if (normalizedAction === PpaTableRowAction.Delete) {
                    confirmDeleteScenario(record.project_id, record);
                    return;
                }
                const scenarioAction = tableRowActionToScenarioAction(normalizedAction);
                if (scenarioAction) {
                    openScenarioModal(record.project_id, scenarioAction, record);
                }
                return;
            }

            if (onAction) {
                onAction(normalizedAction, toPpaPublicRowRecord(record) as ProjectRow | ScenarioRow);
            }
        },
        [canAccessAction, confirmDeleteScenario, onAction, openScenarioModal],
    );

    return {
        scenarioModal,
        setScenarioModal,
        openScenarioModal,
        handleRowAction,
        getActionMenuItems,
    };
};
